import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import DataEdge from "./DataEdge";
import ExecEdge from "./ExecEdge";
import nodeTypes from "../nodeTypes";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";

type Props = {
  /** When true, shows a second exec edge to a third node */
  chain?: boolean;
};

function ExecEdgePreview({ chain }: Props) {
  const nodes: Node[] = [
    {
      id: "start",
      type: "default",
      position: { x: 80, y: 140 },
      data: {
        label: "Start",
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [], out: [] },
        },
      },
    },
    {
      id: "mid",
      type: "default",
      position: { x: 360, y: 140 },
      data: {
        label: "Step",
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [], out: [] },
        },
      },
    },
    ...(chain
      ? [
          {
            id: "end",
            type: "default",
            position: { x: 640, y: 140 },
            data: {
              label: "Done",
              pins: {
                exec: { in: [{ id: "in" }], out: [] },
                data: { in: [], out: [] },
              },
            },
          } as Node,
        ]
      : []),
  ];

  const edges: Edge[] = [
    {
      id: "x-start-mid",
      type: "exec",
      source: "start",
      target: "mid",
      sourceHandle: "exec:out:next",
      targetHandle: "exec:in:in",
      data: { channel: "exec" },
    } as any,
    ...(chain
      ? [
          {
            id: "x-mid-end",
            type: "exec",
            source: "mid",
            target: "end",
            sourceHandle: "exec:out:next",
            targetHandle: "exec:in:in",
            data: { channel: "exec" },
          } as any,
        ]
      : []),
  ];

  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);

  return (
    <div className="h-[720px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/ExecEdge",
  title: "ExecEdge",
  group: "Flows/Edges",
  component: ExecEdgePreview as any,
  description: "Execution edge with consistent styling and arrowhead.",
  defaultProps: { chain: true },
  controls: {
    chain: { type: "boolean", label: "Chain two edges" },
  },
  variants: [
    { name: "Single", props: { chain: false } },
    { name: "Chain", props: { chain: true } },
  ],
};

export default meta;
